// apps/app/src/features/chat/Messages/Item/index.tsx
import { Text } from '@/components/ui/text'
import { Rank } from '@components'
import { useNavigate } from '@solidjs/router'
import dayJs from 'dayjs'
import { type Component, Show, createSignal } from 'solid-js'
import { useService } from 'solid-services'
import { css } from 'styled-system/css'
import { Box, Flex, Grid } from 'styled-system/jsx'
import { useChannel, useUi } from '../../state'
import type { DisplayMessage } from '../../state/resources/getChannelHistory'
import Actions from './Actions'
import { Avatar } from './Avatar'
import Reactions from './Reactions'
import RichText from './RichText'
import { useMessageReactions } from './useMessageReactions' // <-- Imported Hook

const format = 'DD/MM HH:mm'

export const Item: Component<{
  message: DisplayMessage
  isMine?: boolean
  showMessageAuthor?: boolean
  isFocus?: boolean
  isMinimal?: boolean
  isFirstInGroup?: boolean
  isLastInGroup?: boolean
}> = (props) => {
  const navigate = useNavigate()
  const { setScrollTo, timeline } = useService(useChannel)()
  const { channelPathRoot } = useService(useUi)()
  const [isHovering, setisHovering] = createSignal(false)

  // Initialize the shared reaction state for this specific message
  const { localReactions, handleReaction } = useMessageReactions(
    props.message.id,
    props.message.message_reaction
  )

  const profile = props.message.profile
  const profilePrivate = props.message.profile_private as unknown as { role: string }
  const lastUpdated = profile?.last_seen?.updated_at
  const created = props.message.created_at

  const time = () => dayJs(new Date(created)).format(format)

  const navigateToReply = () => {
    const currentTimeline = timeline()
    const isInChat = Boolean(
      currentTimeline.some((msg: DisplayMessage) => msg.id === props.message.parent_id),
    )
    if (!isInChat) {
      navigate(`${channelPathRoot()}/message/${props.message.parent_id}`)
    } else if (props.message.parent_id) {
      setScrollTo({
        id: props.message.parent_id,
        behavior: 'smooth',
        align: 'center',
        isHighlight: true,
      })
    }
  }

  return (
    <Flex
      id={props.message.id}
      gap="11px"
      alignItems="flex-start"
      flexDirection={props.isMine ? 'row-reverse' : 'row'}
      mt={props.isFirstInGroup ? '18px' : '2px'}
      position="relative"
      onMouseEnter={() => setisHovering(true)}
      onMouseLeave={() => setisHovering(false)}
    >
      <Show when={!props.isMinimal}>
        <Box pos="relative" flexShrink={0}>
          <Avatar
            id={props.message.created_by}
            hide={!props.showMessageAuthor}
            lastSeen={lastUpdated}
          />
          <Show when={props.showMessageAuthor && profilePrivate?.role}>
            <Box
              pos="absolute"
              right="-3px"
              bottom="-3px"
              borderRadius="full"
              bg="gray.surface.bg"
              boxShadow="0 0 0 1px var(--colors-editorial-hair-strong)"
            >
              <Rank
                rank={profilePrivate?.role}
                dimensions="15px"
              />
            </Box>
          </Show>
        </Box>
      </Show>
      <Grid gap="3px" minWidth={0} flex="1">
        <Show when={props.showMessageAuthor}>
          <Flex alignItems="baseline" gap="8px" justifySelf={props.isMine ? 'end' : 'start'}>
            <Text
              fontWeight="700"
              fontSize="13.5px"
              color="editorialInk"
            >
              {profile?.username}
            </Text>
            <Text fontSize="10.5px" color="editorialInk3">{time()}</Text>
          </Flex>
        </Show>
        <Show when={props.message.parent_preview_text}>
          <Box
            cursor="pointer"
            onClick={navigateToReply}
            borderLeft="2px solid var(--colors-editorial-hair-strong)"
            bg="editorialQuote"
            borderRadius="0 8px 8px 0"
            px="11px"
            py="8px"
            transition="background 0.2s ease"
            _hover={{ bg: 'editorialHover' }}
            justifySelf={props.isMine ? 'end' : 'start'}
            maxWidth="min(480px, 100%)"
          >
            <Text fontSize="12px" color="editorialInk3" lineHeight="1.45">
              {props.message.parent_preview_text}
            </Text>
          </Box>
        </Show>
        <Flex
          alignItems="flex-start"
          gap="6px"
          flexDirection={props.isMine ? 'row-reverse' : 'row'}
          justifySelf={props.isMine ? 'end' : 'start'}
          maxWidth="min(560px, 100%)"
        >
          <Grid
            gap="6px"
            flex="1"
            minWidth={0}
          >
            <Box
              px="13px"
              py="9px"
              borderRadius={props.isMine ? '14px 4px 14px 14px' : '4px 14px 14px 14px'}
              style={{
                background: props.isMine
                  ? 'color-mix(in srgb, var(--colors-editorial-accent) 12%, var(--colors-editorial-panel))'
                  : 'var(--colors-editorial-quote)',
                border: props.isMine
                  ? '1px solid color-mix(in srgb, var(--colors-editorial-accent) 30%, transparent)'
                  : '1px solid var(--colors-editorial-hair)',
                outline: props.isFocus ? '1px solid var(--colors-jade-10)' : 'none',
                'outline-offset': '2px',
              }}
            >
              <RichText
                content={props.message.content}
                class={css({
                  fontSize: '13.5px !important',
                  lineHeight: '1.5 !important',
                  color: 'editorialInk2',
                })}
              />
            </Box>
            {/* Pass the reactive state and the handler down */}
            <Reactions reactions={localReactions()} onReaction={handleReaction} />
          </Grid>
          <Flex
            alignItems="center"
            gap={0}
            opacity={0}
            transition="opacity 0.3s ease"
            _hover={{ opacity: 1 }}
          >
            {/* Pass the handler down to Actions */}
            <Actions
              isMinimal={props?.isMinimal}
              isMine={props.isMine}
              channelPathRoot={channelPathRoot()}
              message={props.message}
              onReaction={handleReaction}
            />
          </Flex>
        </Flex>
      </Grid>
    </Flex>
  )
}

export default Item
